import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    Fragment,
    startTransition,
} from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { Avatar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
    VisualEditor,
    SimpleQuestionPreviewModal,
    mapSuperApiQuestionToEditor,
    mergeEditorIntoSuperQuestion,
} from '@awarego-ui/components';
import services from '../../services';
import { useStores } from '../../hooks/use-stores';
import ErrorMessage from '../../components/errormessage';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import TagsEditor from './TagsEditor';
import QuestionTranslationsEditor from '../../components/super/QuestionTranslationsEditor';
import ACLEditor from './ACLEditor';

const useStyles = makeStyles(() => ({
    language: {
        filter: 'grayscale(1)',
        opacity: 0.3,
        cursor: 'pointer',
    },
    selectedLanguage: {
        filter: 'grayscale(0)',
        opacity: 1,
    },
}));

function definitionStringForTranslations(definition) {
    if (definition == null) return '';
    if (typeof definition === 'string') return definition;
    try {
        return JSON.stringify(definition, null, 2);
    } catch {
        return '';
    }
}

function SuperVisualQuestionEditor() {
    const { questionId } = useParams();
    const { superStore } = useStores();
    const classes = useStyles();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [loading, setLoading] = useState(Boolean(questionId));
    const [loadError, setLoadError] = useState(null);
    const [rawApi, setRawApi] = useState(null);
    const [initialEditorQuestion, setInitialEditorQuestion] = useState(null);
    const [saving, setSaving] = useState(false);
    const rawRef = useRef(null);
    rawRef.current = rawApi;

    const isEdit = Boolean(questionId);

    const load = useCallback(async () => {
        setLoadError(null);
        if (!questionId) {
            const stub = {
                name: 'New Question',
                definition: JSON.stringify(
                    {
                        type: 1,
                        ui: {
                            blocks: [],
                            presentation: null,
                            minimumBlockCount: 1,
                        },
                        actions: {},
                    },
                    null,
                    2
                ),
                tags: [],
                languages: ['en'],
                area: 'General',
                difficulty: 'basic',
                components: 'Behaviour',
            };
            startTransition(() => {
                setRawApi(stub);
                setInitialEditorQuestion(mapSuperApiQuestionToEditor(stub));
                setLoading(false);
            });
            return;
        }
        setLoading(true);
        try {
            const q = await services.Questions.fetch(questionId);
            startTransition(() => {
                setRawApi(q);
                setInitialEditorQuestion(mapSuperApiQuestionToEditor(q));
            });
        } catch (e) {
            startTransition(() => {
                setLoadError(e?.message || 'Failed to load question');
                setRawApi(null);
                setInitialEditorQuestion(null);
            });
        } finally {
            startTransition(() => {
                setLoading(false);
            });
        }
    }, [questionId]);

    useEffect(() => {
        void superStore.loadLanguages();
    }, [superStore]);

    useEffect(() => {
        void load();
    }, [load]);

    const editorKey = useMemo(
        () => `${questionId || 'new'}-${rawApi?.id || 'draft'}`,
        [questionId, rawApi?.id]
    );

    const renderPreviewModal = useCallback(
        ({ question: previewQuestion, onClose }) => (
            <SimpleQuestionPreviewModal
                question={previewQuestion}
                onClose={onClose}
            />
        ),
        []
    );

    const editingQuestion = rawApi;
    const selectedLanguages = editingQuestion?.languages || ['en'];
    const languages = superStore.sortedlanguages || [];

    const toggleLanguage = (code) => {
        if (!editingQuestion) return;
        if (!editingQuestion.languages) {
            editingQuestion.languages = [...selectedLanguages];
        }
        if (editingQuestion.languages.includes(code)) {
            editingQuestion.languages = editingQuestion.languages.filter(
                (lang) => lang !== code
            );
        } else {
            editingQuestion.languages.push(code);
        }
        setRawApi({ ...editingQuestion });
    };

    const makeCopy = async () => {
        if (!questionId) return;
        const result = await superStore.makeQuestionCopy(questionId);
        if (result?.newId) {
            navigate(`/content/questions/${result.newId}/edit`);
        }
    };

    const handleSave = useCallback(
        async (editorQuestion) => {
            setSaving(true);
            try {
                const base = rawRef.current || {};
                const mergedQ = {
                    ...editorQuestion,
                    tags: base.tags,
                    thumbnail: base.thumbnail,
                    external_id: base.external_id,
                    order_number: base.order_number,
                    unique_id: base.unique_id,
                    acl: base.acl,
                };
                const merged = mergeEditorIntoSuperQuestion(mergedQ, base);
                if (isEdit) {
                    await services.Questions.update(merged, merged.id);
                } else {
                    const created = await services.Questions.create(merged);
                    const newId =
                        created && typeof created === 'object'
                            ? created.id
                            : created;
                    if (newId) {
                        navigate(`/content/questions/${newId}/edit`, {
                            replace: true,
                        });
                        return true;
                    }
                }
                await load();
                return true;
            } catch (e) {
                superStore.setError(e?.message || 'Save failed', 'question');
                return false;
            } finally {
                setSaving(false);
            }
        },
        [rawApi, isEdit, load, navigate, superStore]
    );

    const handleCancel = useCallback(() => {
        navigate('/content/questions');
    }, [navigate]);

    if (loading) {
        return <ThemedSpinner />;
    }
    if (loadError) {
        return <ErrorMessage error={loadError} />;
    }
    if (!initialEditorQuestion) {
        return <ErrorMessage error="No question data" />;
    }

    const translationDefinition = definitionStringForTranslations(
        mergeEditorIntoSuperQuestion(initialEditorQuestion, rawApi || {})
            .definition
    );

    return (
        <Grid container direction="row" justify="center" spacing={2}>
            <Grid item xs={12}>
                <Box mt={2}>
                    <Typography variant="h2" gutterBottom>
                        Question: {initialEditorQuestion.name}
                    </Typography>
                </Box>
            </Grid>
            {isEdit ? (
                <Grid item xs={12}>
                    <Button variant="contained" color="primary" onClick={makeCopy}>
                        Make Copy
                    </Button>
                </Grid>
            ) : null}
            <Grid item xs={10}>
                <TextField
                    label="Thumbnail URL"
                    fullWidth
                    variant="outlined"
                    value={editingQuestion?.thumbnail || ''}
                    onChange={(e) => {
                        const v = e.target.value;
                        setRawApi((prev) => ({ ...(prev || {}), thumbnail: v }));
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Use the editor below for the presentation and interactive
                    blocks. Languages, tags, access control, and translation keys
                    stay on this page; saving persists the full question
                    definition.
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Box
                    style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        overflow: 'hidden',
                        minHeight: 560,
                    }}
                >
                    <VisualEditor
                        key={editorKey}
                        initialQuestion={initialEditorQuestion}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        companyUid={null}
                        accountUid={null}
                        allowSaveWithoutOwnership
                        renderPreviewModal={renderPreviewModal}
                    />
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Accordion defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h4">
                            Question translation keys
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ display: 'block', padding: 0 }}>
                        <QuestionTranslationsEditor definition={translationDefinition} />
                    </AccordionDetails>
                </Accordion>
            </Grid>

            <TagsEditor editingItem={editingQuestion || {}} targetType="video" />

            <Grid item xs={10} container spacing={1}>
                {languages.map((l) => (
                    <Grid
                        key={l.id}
                        item
                        xs={3}
                        className={clsx(classes.language, {
                            [classes.selectedLanguage]: selectedLanguages.includes(
                                l.code
                            ),
                        })}
                        onClick={() => toggleLanguage(l.code)}
                    >
                        <Avatar alt="country flag" src={`${l.flag_image}`} />
                        {l.name}
                    </Grid>
                ))}
            </Grid>

            {pathname.includes('edit') ? (
                <Fragment>
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            Access control
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <ACLEditor
                            acl={editingQuestion?.acl}
                            unique_id={editingQuestion?.unique_id}
                        />
                    </Grid>
                </Fragment>
            ) : null}

            {superStore.questionsError && (
                <Grid item xs={12}>
                    <ErrorMessage error={superStore.questionsError} />
                </Grid>
            )}
            {saving ? (
                <Grid item xs={12}>
                    <ThemedSpinner />
                </Grid>
            ) : null}
        </Grid>
    );
}

export default observer(SuperVisualQuestionEditor);
